import type { ChatAction, ChatContext, ChatIntent, ChatResponse, ChatMatch, ChatApplication } from "./types";

const DEFAULT_JOBS_ROUTE = "/matches";
const APPLICATIONS_ROUTE = "/applications";

const STATUS_LABELS: Record<string, string> = {
  applied: "Skickad",
  pending: "Pågående",
  processing: "Pågår",
  failed: "Misslyckad",
  cancelled: "Avbruten",
};

export const buildChatResponse = (
  intent: ChatIntent,
  context: ChatContext,
  now: Date = new Date(),
): ChatResponse => {
  switch (intent) {
    case "best_job_today":
    case "best_match":
      return buildBestMatchResponse(intent, context, now);
    case "my_applications":
      return buildMyApplicationsResponse(context);
    case "search_jobs":
      return buildSearchJobsResponse();
    case "applied_status":
      return buildAppliedStatusResponse(context);
    case "why_this_job":
      return buildWhyThisJobResponse(context);
    case "can_apply":
      return buildCanApplyResponse(context);
    case "next_steps":
      return buildPausedNextStepsResponse();
    default:
      return buildFallbackResponse();
  }
};

const buildBestMatchResponse = (
  intent: ChatIntent,
  context: ChatContext,
  now: Date,
): ChatResponse => {
  if (context.matchesLoading) {
    return {
      message: "Jag hämtar dina matchningar från Matchningar. Prova igen om en stund.",
      actions: [navigateAction(intent, "Visa matchningar", DEFAULT_JOBS_ROUTE)],
    };
  }

  const matches = context.matches || [];
  if (matches.length === 0) {
    return {
      message:
        "Jag har inga matchningar att visa. De bygger på din profil – använd Hitta jobb eller Matchningar för att se jobb.",
      actions: [
        refetchAction(intent, "Uppdatera matchning"),
        navigateAction(intent, "Visa matchningar", DEFAULT_JOBS_ROUTE),
      ],
    };
  }

  const recentMatches = intent === "best_job_today" ? filterRecent(matches, 7, now) : matches;
  const sourceMatches = recentMatches.length > 0 ? recentMatches : matches;
  const topMatches = selectTopMatches(sourceMatches, 3, now);
  const n = topMatches.length;
  const sourceLine =
    n > 0
      ? `Jag visar ${n} jobb från dina matchningar (sidan Matchningar):`
      : "Baserat på dina matchningar:";
  const prefix =
    intent === "best_job_today" && recentMatches.length === 0
      ? "Topp 3 just nu:"
      : intent === "best_job_today"
      ? "Bästa jobb att söka idag:"
      : "Topp 3:";
  const lines = [sourceLine, prefix, ...topMatches.map(formatMatchLine)];
  const message = lines.slice(0, 5).join("\n");

  return {
    message,
    actions: [
      buildOpenActionForMatch(intent, topMatches[0]),
      navigateAction(intent, "Visa matchningar", DEFAULT_JOBS_ROUTE),
    ],
  };
};

const buildMyApplicationsResponse = (context: ChatContext): ChatResponse => {
  if (context.applicationsLoading) {
    return {
      message: "Jag hämtar dina ansökningar...",
      actions: [navigateAction("my_applications", "Öppna ansökningar", APPLICATIONS_ROUTE)],
    };
  }
  const applications = context.applications || [];
  if (applications.length === 0) {
    return {
      message: "Du har inga ansökningar än. När du söker jobb eller använder Snabbansökan visas de här.",
      actions: [navigateAction("my_applications", "Till Ansökningar", APPLICATIONS_ROUTE)],
    };
  }
  const count = applications.length;
  const message =
    count === 1
      ? "Du har 1 ansökan. Se den under Ansökningar."
      : `Du har ${count} ansökningar. Se alla under Ansökningar.`;
  return {
    message,
    actions: [navigateAction("my_applications", "Se alla ansökningar", APPLICATIONS_ROUTE)],
  };
};

const buildSearchJobsResponse = (): ChatResponse => ({
  message:
    "Sök jobb med sökfältet högst upp på sidan (Hitta jobb). Skriv titel, företag eller plats. Du kan också använda filtren för plats, roll och annat.",
  actions: [navigateAction("search_jobs", "Till Hitta jobb", "/dashboard")],
});

const buildAppliedStatusResponse = (context: ChatContext): ChatResponse => {
  const job = context.currentJob;
  if (!job?.jobId) {
    return {
      message: "Jag behöver att du öppnar en jobbannons först.",
      actions: [navigateAction("applied_status", "Till jobb", DEFAULT_JOBS_ROUTE)],
    };
  }

  if (context.applicationsLoading) {
    return {
      message: "Jag hämtar dina ansökningar. Försök igen strax.",
      actions: [navigateAction("applied_status", "Gå till ansökningar", APPLICATIONS_ROUTE)],
    };
  }

  const applications = context.applications || [];
  const application = applications.find((app) => app.jobId === job.jobId);
  if (application) {
    const dateText = formatDateShort(application.appliedAt);
    const statusText = formatStatus(application.status);
    const statusSuffix = statusText ? ` Status: ${statusText}.` : "";
    const dateSuffix = dateText ? ` du sökte ${dateText}.` : " du har sökt detta jobbet.";
    return {
      message: `Ja,${dateSuffix}${statusSuffix}`,
      actions: [navigateAction("applied_status", "Gå till ansökningar", APPLICATIONS_ROUTE)],
    };
  }

  return {
    message: "Nej, jag hittar ingen ansökan för detta jobbet.",
    actions: [buildOpenActionForJob("applied_status", "Starta ansökan", job)],
  };
};

const buildWhyThisJobResponse = (context: ChatContext): ChatResponse => {
  const job = context.currentJob;
  if (!job?.jobId) {
    return {
      message: "Jag behöver att du öppnar en jobbannons först.",
      actions: [navigateAction("why_this_job", "Till jobb", DEFAULT_JOBS_ROUTE)],
    };
  }

  const reasons = (job.matchReasons || []).filter(Boolean).slice(0, 3);
  if (reasons.length === 0) {
    return {
      message: "Jag har inte match-data för detta jobb än.",
      actions: [
        refetchAction("why_this_job", "Uppdatera matchning"),
        navigateAction("why_this_job", "Visa matchningar", DEFAULT_JOBS_ROUTE),
      ],
    };
  }

  const message = ["Det här matchar dig eftersom:", ...reasons.map((r) => `- ${r}`)].join("\n");
  return {
    message,
    actions: [
      buildOpenActionForJob("why_this_job", "Öppna jobbet", job),
      navigateAction("why_this_job", "Gå till matchningar", DEFAULT_JOBS_ROUTE),
    ],
  };
};

const buildCanApplyResponse = (context: ChatContext): ChatResponse => {
  const job = context.currentJob;
  if (!job?.jobId) {
    return {
      message: "Jag behöver att du öppnar en jobbannons först.",
      actions: [navigateAction("can_apply", "Till jobb", DEFAULT_JOBS_ROUTE)],
    };
  }

  const profile = context.profile;
  const decision = decideEligibility(job, profile);
  if (decision.status === "unknown") {
    return {
      message: "Oklart. Annonsen säger inte det.",
      actions: [externalOrJobAction("can_apply", "Visa annonsen", job)],
    };
  }

  if (decision.status === "no") {
    return {
      message: decision.message,
      actions: [externalOrJobAction("can_apply", "Visa annonsen", job)],
    };
  }

  return {
    message: decision.message,
    actions: [buildOpenActionForJob("can_apply", "Öppna jobbet", job)],
  };
};

const buildPausedNextStepsResponse = (): ChatResponse => ({
  message: "Att-göra är pausad just nu.",
  actions: [navigateAction("next_steps", "Öppna matchningar", DEFAULT_JOBS_ROUTE)],
});

const DASHBOARD_ROUTE = "/dashboard";

const HOUR_MS = 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * HOUR_MS;

function formatAppliedAtRelative(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const ms = now.getTime() - date.getTime();
  if (ms < 0) return "";
  if (ms < HOUR_MS) {
    const min = Math.floor(ms / 60000);
    return min <= 1 ? "nyss" : `för ${min} min sedan`;
  }
  if (ms < TWENTY_FOUR_HOURS_MS) {
    const h = Math.floor(ms / HOUR_MS);
    return h === 1 ? "för 1 timme sedan" : `för ${h} timmar sedan`;
  }
  const sameDay = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  if (sameDay) {
    const h = String(date.getHours()).padStart(2, "0");
    const m = String(date.getMinutes()).padStart(2, "0");
    return `idag ${h}:${m}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear()) {
    return "igår";
  }
  return formatDateShort(iso) || "";
}

const buildFallbackResponse = (): ChatResponse => ({
  message:
    "Jag kan svara på: Har jag sökt? Varför visas jobbet? För att söka jobb, använd Hitta jobb.",
  actions: [
    navigateAction("fallback", "Öppna Hitta jobb", DASHBOARD_ROUTE),
    navigateAction("fallback", "Öppna matchningar", DEFAULT_JOBS_ROUTE),
    navigateAction("fallback", "Öppna ansökningar", APPLICATIONS_ROUTE),
  ],
});

const decideEligibility = (
  job: NonNullable<ChatContext["currentJob"]>,
  profile?: ChatContext["profile"] | null,
): { status: "yes" | "no" | "unknown"; message: string } => {
  const workMode = (job.workMode || "").toLowerCase();
  const hasRemoteSignal = job.remote === true || workMode.includes("remote") || workMode.includes("distans");
  const hasOnsiteSignal = workMode.includes("onsite") || workMode.includes("på plats");
  const jobLocation = (job.location || "").toLowerCase();
  const profileLocation = (profile?.location || "").toLowerCase();
  const locationMatch =
    jobLocation && profileLocation
      ? jobLocation.includes(profileLocation) || profileLocation.includes(jobLocation)
      : false;
  const remoteOk = profile?.remoteOk ?? null;
  const canTravel = profile?.canTravel ?? null;

  if (!profile || (!jobLocation && job.remote == null && !workMode)) {
    return { status: "unknown", message: "Oklart. Annonsen säger inte det." };
  }

  if (hasRemoteSignal && remoteOk === false && !locationMatch) {
    return { status: "no", message: "Nej. Jobbet verkar vara remote och du har valt bort det." };
  }

  if (!hasRemoteSignal && hasOnsiteSignal && jobLocation && !locationMatch && canTravel === false) {
    return { status: "no", message: "Nej. Jobbet verkar kräva plats på orten." };
  }

  if (hasRemoteSignal && (remoteOk === true || remoteOk === null)) {
    return { status: "yes", message: "Ja. Jobbet är remote enligt annonsen." };
  }

  if (locationMatch) {
    return { status: "yes", message: "Ja. Platsen matchar din profil." };
  }

  if (canTravel === true && jobLocation) {
    return { status: "yes", message: "Ja, om resor funkar för dig." };
  }

  return { status: "unknown", message: "Oklart. Annonsen säger inte det." };
};

const selectTopMatches = (matches: ChatMatch[], limit: number, now: Date): ChatMatch[] => {
  return [...matches]
    .sort((a, b) => {
      const scoreDiff = (b.matchScore || 0) - (a.matchScore || 0);
      if (scoreDiff !== 0) return scoreDiff;
      const dateA = getMatchTimestamp(a, now);
      const dateB = getMatchTimestamp(b, now);
      return dateB - dateA;
    })
    .slice(0, limit);
};

const getMatchTimestamp = (match: ChatMatch, now: Date): number => {
  const dateValue = match.firstSeenAt || match.publishedAt;
  if (!dateValue) return now.getTime();
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? now.getTime() : date.getTime();
};

const filterRecent = (matches: ChatMatch[], days: number, now: Date): ChatMatch[] => {
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  return matches.filter((match) => {
    const matchDate = match.firstSeenAt || match.publishedAt;
    if (!matchDate) return false;
    const date = new Date(matchDate);
    return !Number.isNaN(date.getTime()) && date >= cutoff;
  });
};

const formatMatchLine = (match: ChatMatch, index: number): string => {
  const company = match.company ? ` — ${match.company}` : "";
  const score = match.matchScore ? ` (${match.matchScore}%)` : "";
  const reasonTags = (match.matchReasons || []).filter(Boolean).slice(0, 2);
  const reasons = reasonTags.length > 0 ? ` • ${reasonTags.join(", ")}` : "";
  return `${index + 1}) ${match.title}${company}${score}${reasons}`;
};

const buildOpenActionForMatch = (intent: ChatIntent, match: ChatMatch): ChatAction => {
  const isQuickApply = match.autoApplyReady === true;
  const targetUrl = match.applyUrl || match.sourceUrl || null;

  if (!isQuickApply && targetUrl) {
    return {
      id: buildActionId(intent, "Öppna #1"),
      label: "Öppna #1",
      type: "open_external",
      url: targetUrl,
    };
  }

  return navigateAction(intent, "Öppna #1", buildJobLink(match.id, DEFAULT_JOBS_ROUTE));
};

const buildOpenActionForJob = (
  intent: ChatIntent,
  label: string,
  job: NonNullable<ChatContext["currentJob"]>,
): ChatAction => {
  const isQuickApply = job.autoApplyReady === true;
  const targetUrl = job.applyUrl || job.sourceUrl || null;

  if (!isQuickApply && targetUrl) {
    return {
      id: buildActionId(intent, label),
      label,
      type: "open_external",
      url: targetUrl,
    };
  }

  return navigateAction(intent, label, buildJobLink(job.jobId, job.route));
};

const formatDateShort = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatStatus = (status?: string | null): string => {
  if (!status) return "";
  return STATUS_LABELS[status] || status;
};

const buildJobLink = (jobId: string, route?: string | null): string => {
  const base = route && route.startsWith("/") ? route : DEFAULT_JOBS_ROUTE;
  const url = new URL(base, "https://findigo.local");
  url.searchParams.set("jobId", jobId);
  return url.pathname + url.search;
};

const navigateAction = (intent: ChatIntent, label: string, href: string): ChatAction => ({
  id: buildActionId(intent, label),
  label,
  type: "navigate",
  href,
});

const refetchAction = (intent: ChatIntent, label: string): ChatAction => ({
  id: buildActionId(intent, label),
  label,
  type: "refetch_matches",
});

const externalOrJobAction = (intent: ChatIntent, label: string, job: NonNullable<ChatContext["currentJob"]>): ChatAction => {
  if (job.applyUrl || job.sourceUrl) {
    return {
      id: buildActionId(intent, label),
      label,
      type: "open_external",
      url: job.applyUrl || job.sourceUrl || undefined,
    };
  }
  return navigateAction(intent, label, buildJobLink(job.jobId, job.route));
};

const buildActionId = (intent: ChatIntent, label: string): string =>
  `${intent}-${label.toLowerCase().replace(/\s+/g, "-")}`;
